import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useSession } from "../lib/session";
import { listCompetitions, myCompetitionIds } from "../lib/api";
import { Loading, Notice, Empty, PageHead } from "../components/ui";

/**
 * Where "/" lands.
 *
 * One competition running and you're in it: straight there, because
 * choosing from a list of one is a tap that teaches nothing. More than
 * one, or none: a short list, which is the only time the choice matters.
 */
export default function Pick() {
  const { society, profile, isOrganiser } = useSession();
  const [comps, setComps] = useState(null);
  const [mine, setMine] = useState(new Set());
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      listCompetitions(society.id),
      profile ? myCompetitionIds(society.id, profile.id) : Promise.resolve(new Set()),
    ]).then(([c, m]) => { setComps(c); setMine(m); })
      .catch((e) => setError(e.message));
  }, [society.id, profile?.id]);

  if (error) return <Notice kind="error">{error}</Notice>;
  if (!comps) return <Loading />;

  const running = comps.filter((c) => c.status !== "completed");
  const mineRunning = running.filter((c) => mine.has(c.id));

  // the common case: one thing on, and you're in it
  if (mineRunning.length === 1) return <Navigate to={`/c/${mineRunning[0].id}/today`} replace />;
  if (mineRunning.length === 0 && running.length === 1 && isOrganiser) {
    return <Navigate to={`/c/${running[0].id}/today`} replace />;
  }

  return (
    <>
      <PageHead title={society.name} sub={`${running.length} on at the moment`} />

      {running.length === 0 ? (
        <Empty title="Nothing on at the moment"
               action={isOrganiser && (
                 <Link className="btn btn-primary" to="/competitions/new"
                       style={{ textDecoration: "none" }}>Set one up</Link>)}>
          {isOrganiser
            ? "Set up a competition and the season starts here."
            : "Your organiser will enter you in the next competition."}
        </Empty>
      ) : (
        <div className="stack" style={{ gap: ".6rem" }}>
          {running.map((c) => (
            <Link to={`/c/${c.id}/today`} className="pickrow" key={c.id}>
              <span className="pickrow-text">
                <strong>{c.name}</strong>
                <small>
                  {[(c.rounds ?? []).length === 1 ? "one round"
                      : `${(c.rounds ?? []).length} rounds`,
                    mine.has(c.id) ? "you're in it" : "not entered"]
                    .filter(Boolean).join(" · ")}
                </small>
              </span>
              <span className="pickrow-chev" aria-hidden="true" />
            </Link>
          ))}
        </div>
      )}

      <p className="muted" style={{ fontSize: ".75rem", marginTop: "1rem", lineHeight: 1.6 }}>
        Finished competitions, members and courses are on the society's own
        pages, behind your initials.
      </p>
    </>
  );
}
